import { ControlChartGraphRecordDto } from "src/api/generated/spc/graphs/controlChartGraphRecordDto.ts";
import { AsyncMenuButton } from "src/components/common/contextMenu/AsyncMenuButton.tsx";
import i18n from "i18next";
import { faBan, faCalculator, faLink, faSquareRootAlt, faWrench } from "@fortawesome/pro-regular-svg-icons";
import { useTenantCustomizations } from "src/tenantCustomizations/TenantCustomizationsContext.ts";
import { DocumentsOfObjectButton } from "src/components/views/documents/objectDocuments/DocumentsOfObjectButton.tsx";
import { useInputDialog } from "src/components/common/dialogs/input/useInputDialog.tsx";
import dayjs from "dayjs";
import { askInputAndOpenControlChartCapabilityView } from "src/components/views/spc/controlChart/utils.tsx";
import { SpcRecordActionForm } from "src/components/views/spc/controlChart/controlChartInspectingView/SpcRecordActionForm.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { ControlChartInspectingViewContext } from "src/components/views/spc/controlChart/controlChartInspectingView/ControlChartInspectingViewContext.ts";
import { useContext } from "react";
import { ControlChartCommonApi } from "src/api/generated/spc/controlChart/api/controlChartCommonApi.ts";
import { ConnectProcessImprovementToRawRecordView } from "src/components/views/spc/processImprovement/ConnectProcessImprovementToRawRecordView.tsx";
import { ProcessImprovementsView } from "src/components/views/spc/processImprovement/ProcessImprovementsView.tsx";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { openControlChartLimitCalculationDialog } from "../analysis/controlChartAnalysisDialogs.tsx";

export interface ControlChartGraphDataPointContextMenuProps {
	record: ControlChartGraphRecordDto;
	refreshGraph: () => Promise<unknown>;
}

export const ControlChartGraphDataPointContextMenu = ({
	record,
	refreshGraph,
}: ControlChartGraphDataPointContextMenuProps) => {
	const { openDialog } = useGenericDialog();
	const showInputDialog = useInputDialog();
	const { tenantConfig } = useTenantCustomizations();
	const hasPermission = useHasPermission();

	const { unresolvedSpecialCausesDataGridRefreshRef } = useContext(ControlChartInspectingViewContext) ?? {};
	const { recordId, controlChartId, processImprovementId, savedSpecialCauseCode, chartName, observationTime } =
		record;

	return [
		<AsyncMenuButton
			key={"disableRecord"}
			label={i18n.t("disable")}
			icon={faBan}
			onClick={async () => {
				await ControlChartCommonApi.disableRecord({
					recordId: recordId,
				});
				await refreshGraph();
			}}
		/>,
		<AsyncMenuButton
			key={"action"}
			label={i18n.t("spc_action")}
			icon={faWrench}
			onClick={() => {
				openDialog(({ closeDialog, onContentEdited }) => ({
					title: i18n.t("spc_action"),
					size: "sm",
					agentContext: {
						key: defineAgentUiContextKey("spc.controlChart.recordAction.dialog"),
						data: { controlChartId, recordIds: [recordId] },
						pathSegments: [i18n.t("spc_action")],
					},
					content: (
						<SpcRecordActionForm
							recordIds={[recordId]}
							onFormEdited={onContentEdited}
							onCompleted={async () => {
								await closeDialog();
								await Promise.all([
									refreshGraph(),
									unresolvedSpecialCausesDataGridRefreshRef?.refresh(),
								]);
							}}
						/>
					),
				}));
			}}
		/>,
		<AsyncMenuButton
			key={"calculateLimits"}
			label={i18n.t("calculate_control_limits")}
			icon={faCalculator}
			onClick={() => {
				openControlChartLimitCalculationDialog({
					openDialog,
					controlChartId: controlChartId,
					recordId: recordId,
					onSubmit: () => refreshGraph(),
				});
			}}
		/>,
		<AsyncMenuButton
			key={"openStatisticsView"}
			label={i18n.t("capability")}
			icon={faSquareRootAlt}
			onClick={async () => {
				await askInputAndOpenControlChartCapabilityView({
					showInputDialog,
					openDialog,
					controlChartId: controlChartId,
					chartName: chartName,
					beforeDate: dayjs(observationTime),
				});
			}}
		/>,
		tenantConfig.spc.processImprovementsEnabled &&
			hasPermission(Permissions.Spc.ProcessImprovements.MANAGE) && [
				savedSpecialCauseCode != null && processImprovementId == null && (
					<AsyncMenuButton
						key={"connectToProcessImprovement"}
						label={i18n.t("connect_to_an_improvement")}
						icon={faLink}
						onClick={async () => {
							openDialog(({ closeDialog }) => ({
								title: i18n.t("explanations"),
								size: "xl",
								agentContext: {
									key: defineAgentUiContextKey("spc.processImprovement.connectRawRecord.dialog"),
									data: { controlChartId, rawRecordId: recordId },
									pathSegments: [i18n.t("connect_to_an_improvement")],
								},
								content: (
									<ConnectProcessImprovementToRawRecordView
										rawRecordId={recordId}
										onSubmit={async () => {
											await refreshGraph();
											await closeDialog();
										}}
									/>
								),
							}));
						}}
					/>
				),
				processImprovementId != null && (
					<AsyncMenuButton
						key={"openImprovement"}
						label={i18n.t("open_improvement")}
						icon={faLink}
						onClick={() => {
							openDialog({
								title: i18n.t("explanations"),
								size: "xl",
								agentContext: {
									key: defineAgentUiContextKey("spc.processImprovements.dialog"),
									data: { processImprovementId },
									pathSegments: [i18n.t("open_improvement")],
								},
								content: (
									<ProcessImprovementsView
										onlyProcessImprovementId={processImprovementId}
										selectFirstImprovementOnLoad={true}
									/>
								),
							});
						}}
					/>
				),
			],
		tenantConfig.documentsEnabled && (
			<DocumentsOfObjectButton
				key={"openRecordDocuments"}
				objectRef={{
					objectType: "SPC_RECORD",
					objectId: recordId,
				}}
				label={i18n.t("documents")}
				variant={"menu"}
			/>
		),
	];
};
